/**
 * Hygiene Bundle — one reversible, risk-clustered approval envelope for a
 * whole CRM-hygiene run.
 *
 * Drafted fixes are clustered by severity with an injected blast-radius
 * measure (à la policyBlastRadius: the engine never re-implements risk
 * scoring — it is passed in), so triage becomes one gesture per band:
 * approve the forty low-risk fixes in one click, scrutinize the high-risk
 * ones individually — the same economics approvalTriage gives the inbox.
 *
 * Reversibility is structural, not aspirational: {@link reversalPlan}
 * compiles the exact inverse diff for every draft, in reverse application
 * order, so composing plan-after-apply restores the original state
 * byte-for-byte (locked by a fixture round-trip test).
 *
 * PURE: no store, no firebase, no clock. The only import beyond types is
 * the shipped FNV-1a digest so bundle ids hash with the same approach the
 * tamper-evident ledger chain seals with.
 */

import { fnvHash } from '../ledger/verifyChain';
import type { ActionDiff, FieldChange } from './inlineEditDiff';
import type { DraftedFix } from './hygieneDrafter';
import type { HygieneSeverity } from './hygieneScanner';

// ===========================================================================
// Types
// ===========================================================================

/** One risk band: every diff in it shares a severity; radius is the sum. */
export type HygieneCluster = {
  severity: string;
  /** Sum of the injected blast radius over the cluster's diffs. */
  blastRadius: number;
  diffs: ActionDiff[];
};

export type HygieneBundle = {
  /** Deterministic id — same drafts (any order) → same bundle id. */
  id: string;
  drafts: DraftedFix[];
  /** Risk-clustered view, highest severity first. */
  clusters: HygieneCluster[];
  /** Per-finding blast radius, sealed at build time so rejectFinding can
   *  recluster without re-running the injected measure. */
  radii: Record<string, number>;
};

/** Highest-risk-first ordering; unknown severities sort last, alphabetic. */
const SEVERITY_ORDER: Record<HygieneSeverity, number> = { high: 0, med: 1, low: 2 };

function severityRank(s: string): number {
  return SEVERITY_ORDER[s as HygieneSeverity] ?? 3;
}

// ===========================================================================
// buildBundle
// ===========================================================================

function clustersFrom(
  drafts: DraftedFix[],
  radiusOf: (findingId: string) => number,
): HygieneCluster[] {
  const bySeverity = new Map<string, DraftedFix[]>();
  for (const d of drafts) {
    const key = d.finding.severity;
    const list = bySeverity.get(key) ?? [];
    list.push(d);
    bySeverity.set(key, list);
  }
  return [...bySeverity.entries()]
    .sort(
      (a, b) => severityRank(a[0]) - severityRank(b[0]) || a[0].localeCompare(b[0]),
    )
    .map(([severity, members]) => {
      // Within a band, the widest blast radius leads — it deserves the
      // first look even inside a one-click cluster.
      const ordered = [...members].sort(
        (a, b) =>
          radiusOf(b.finding.id) - radiusOf(a.finding.id) ||
          a.finding.id.localeCompare(b.finding.id),
      );
      return {
        severity,
        blastRadius: ordered.reduce((sum, d) => sum + radiusOf(d.finding.id), 0),
        diffs: ordered.map((d) => d.diff),
      };
    });
}

/**
 * Cluster drafted fixes into one triage-ready bundle. `blastRadiusOf` is
 * injected (never re-implemented here — same honesty rule as
 * policyBlastRadius) and evaluated exactly once per draft; the result is
 * sealed into `radii`. Pure: inputs are never mutated.
 */
export function buildBundle(
  drafts: DraftedFix[],
  blastRadiusOf: (d: ActionDiff) => number,
): HygieneBundle {
  const radii: Record<string, number> = {};
  for (const d of drafts) {
    radii[d.finding.id] = blastRadiusOf(d.diff);
  }
  const idSeed = drafts
    .map((d) => d.finding.id)
    .sort()
    .join('|');
  return {
    id: `hygiene:${fnvHash('', idSeed)}`,
    drafts: [...drafts],
    clusters: clustersFrom(drafts, (fid) => radii[fid] ?? 0),
    radii,
  };
}

// ===========================================================================
// Triage gestures
// ===========================================================================

/**
 * One-gesture cluster approval: returns EXACTLY that cluster's diffs (a
 * copy) for the caller to route through the existing governed pipeline
 * (queueApproval → resolveApproval). Out-of-range index → []. Never writes.
 */
export function approveCluster(bundle: HygieneBundle, clusterIndex: number): ActionDiff[] {
  const cluster = bundle.clusters[clusterIndex];
  return cluster ? [...cluster.diffs] : [];
}

/**
 * Remove one finding from the bundle and recluster from the sealed radii —
 * a rejected draft costs nothing and leaves everything else untouched.
 * Unknown id returns an equivalent bundle. Pure: input never mutated;
 * the bundle id is preserved (identity of the RUN, not of its contents).
 */
export function rejectFinding(bundle: HygieneBundle, findingId: string): HygieneBundle {
  const drafts = bundle.drafts.filter((d) => d.finding.id !== findingId);
  const radii: Record<string, number> = {};
  for (const d of drafts) radii[d.finding.id] = bundle.radii[d.finding.id] ?? 0;
  return {
    id: bundle.id,
    drafts,
    clusters: clustersFrom(drafts, (fid) => radii[fid] ?? 0),
    radii,
  };
}

// ===========================================================================
// Reversal
// ===========================================================================

/** Render one value for a summary line — honest '—' for empty. */
function show(v: unknown): string {
  if (v === null || v === undefined) return '—';
  const s = typeof v === 'string' ? v : String(v);
  return s.trim() === '' ? '—' : s;
}

function changeLine(c: FieldChange): string {
  return show(c.from) === '—'
    ? `${c.attr} → ${show(c.to)}`
    : `${c.attr}: ${show(c.from)} → ${show(c.to)}`;
}

/** The exact inverse of one diff: every change swapped to → from. */
export function invertDiff(diff: ActionDiff): ActionDiff {
  const changes: FieldChange[] = [...diff.changes]
    .reverse()
    .map((c) => ({ attr: c.attr, from: c.to, to: c.from }));
  return {
    recordId: diff.recordId,
    changes,
    summary: changes.map(changeLine).join('; '),
  };
}

/**
 * Compile the atomic rollback for the WHOLE bundle: the inverse of every
 * draft's diff, in reverse application order, so applying the bundle then
 * its reversal plan restores the original state exactly (undo composes
 * right-to-left). Like reversal.ts, this produces PROPOSAL data — the
 * caller routes it through the same governed approval pipeline, never a
 * direct write.
 */
export function reversalPlan(bundle: HygieneBundle): ActionDiff[] {
  return [...bundle.drafts].reverse().map((d) => invertDiff(d.diff));
}
